import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCourseById } from '../../data/courses';
import { getPlaygroundCourse } from '../../data/playgroundData';
import { loadAllProgress, type AllCoursesProgress } from '../../services/progressService';
import { fetchMyCohorts, type Cohort } from '../../services/cohortService';
import MatrixBackground from '../../components/ui/MatrixBackground';

export default function CourseDetail() {
  const { id, cohortCode } = useParams<{ id: string; cohortCode?: string }>();
  const location = useLocation();
  const course = getCourseById(id || '');
  const [openModules, setOpenModules] = useState<Set<number>>(new Set([0]));
  const [progress, setProgress] = useState<AllCoursesProgress | null>(null);
  const [cohort, setCohort] = useState<Cohort | null>(null);
  const [redirectCohort, setRedirectCohort] = useState<string | null>(null);
  const [cohortDenied, setCohortDenied] = useState(false);
  const [cohortLoading, setCohortLoading] = useState(false);
  const isBootcampRoute = location.pathname.startsWith('/bootcamps');

  useEffect(() => {
    loadAllProgress().then(p => { if (p) setProgress(p); });
  }, []);

  useEffect(() => {
    if (!isBootcampRoute) return;
    setCohortLoading(true);
    fetchMyCohorts().then(cohorts => {
      if (cohortCode) {
        const match = cohorts.find(c => c.cohort_code === cohortCode);
        if (match) { setCohort(match); setCohortDenied(false); }
        else setCohortDenied(true);
      } else if (cohorts.length > 0) {
        const match = cohorts.find(c => c.bootcamp_id === id);
        if (match) setRedirectCohort(match.cohort_code);
      }
    }).finally(() => setCohortLoading(false));
  }, [cohortCode, id, isBootcampRoute]);

  if (!course) return <Navigate to={isBootcampRoute ? '/bootcamps' : '/cursos'} replace />;

  if (isBootcampRoute && cohortLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-green-500/30 border-t-green-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (cohortDenied) return <Navigate to="/bootcamps" replace />;

  if (isBootcampRoute && !cohortCode && redirectCohort) {
    return <Navigate to={`/bootcamps/${id}/${redirectCohort}`} replace />;
  }

  if (course.category === 'bootcamp' && !isBootcampRoute) {
    return <Navigate to={`/bootcamps/${id}`} replace />;
  }

  const cohortFlags = cohort ? { weeksTheoricUnlocked: cohort.weeks_theoric_unlocked, weeksGroupUnlocked: cohort.weeks_group_unlocked, startsAt: cohort.starts_at } : undefined;
  const pgCourse = getPlaygroundCourse(id || '', cohortFlags);
  const hasPlayground = !!pgCourse;
  const totalLessons = pgCourse?.lessons.length ?? 0;
  const courseProgress = progress?.[id || ''];
  const completedCount = courseProgress?.totalCompleted ?? 0;
  const pct = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;
  const hasStarted = !!courseProgress;
  const resumeIdx = courseProgress?.lastLessonIndex ?? 0;
  const pathPrefix = isBootcampRoute ? '/bootcamps' : '/cursos';
  const cohortSegment = cohortCode ? `/${cohortCode}` : '';
  const startLink = course.contentLink || (hasPlayground ? `${pathPrefix}/${id}${cohortSegment}/playground` : pathPrefix);

  const toggleModule = (idx: number) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const totalTopics = course.modules.reduce((sum, m) => sum + m.topics.length, 0);

  return (
    <div className="min-h-screen bg-black font-sans">
      {/* Nav */}
      <nav className="fixed top-0 w-full bg-black/80 backdrop-blur-md border-b border-green-500/30 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3 flex justify-between items-center">
          <Link to="/" className="text-xl font-bold text-green-400">Luciano Argolo</Link>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-green-400/70 hover:text-green-300 transition-colors">Home</Link>
            <Link to="/cursos" className="text-green-400 hover:text-green-300 transition-colors font-semibold">Cursos</Link>
            <Link to="/bootcamps" className="text-green-400/70 hover:text-green-300 transition-colors">Bootcamps</Link>
            <Link to="/mentorias" className="text-green-400/70 hover:text-green-300 transition-colors">Mentoría</Link>
            <Link to="/cursos" className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 text-sm rounded transition-all duration-300 transform hover:scale-105">Mis cursos</Link>
          </div>
        </div>
      </nav>

      {/* ===== Hero: Video + Info (Analyst Builder style) ===== */}
      <section className="relative pt-24 pb-8 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex flex-col items-center gap-8 rounded-lg lg:flex-row lg:items-start lg:gap-12 lg:bg-gray-900/60 lg:p-10 lg:border lg:border-green-500/10">

            {/* Video */}
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="w-full sm:max-w-xl lg:w-1/2">
              <div className="aspect-video w-full rounded-xl overflow-hidden border-2 border-dashed border-green-500/25 bg-gray-900/80 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-green-500/50 transition-colors group">
                {course.videoPlaceholder ? (
                  <iframe src={course.videoPlaceholder} title={`Video introductorio - ${course.title}`} className="w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-full bg-green-500/10 border-2 border-green-500/30 flex items-center justify-center group-hover:bg-green-500/20 group-hover:border-green-500/50 transition-all">
                      <svg className="w-7 h-7 fill-green-400 ml-0.5" viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                    </div>
                    <span className="text-sm text-gray-500 font-medium">Video introductorio</span>
                    <span className="text-xs text-gray-600">Próximamente</span>
                  </>
                )}
              </div>
            </motion.div>

            {/* Info */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex w-full flex-col items-start gap-5 lg:w-1/2">
              <h1 className="text-2xl font-bold leading-none text-green-400 md:text-[32px]">
                {course.title}
              </h1>

              {/* Meta pills (AB style rounded pills) */}
              <div className="flex flex-wrap gap-3">
                <span className="flex items-center gap-2 rounded-full bg-gray-800 px-3 py-2 text-xs font-medium text-white">
                  <svg className="h-4 w-4 fill-white" viewBox="0 0 24 24" stroke="none"><circle cx="12" cy="12" r="10" fill="currentColor" /><circle cx="12" cy="12" r="8.5" fill="#1f2937" /><polyline points="12 7 12 12 15.5 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                  {course.weeks}
                </span>
                <span className="flex items-center gap-2 rounded-full bg-gray-800 px-3 py-2 text-xs font-medium text-white">
                  <svg className="h-4 w-4 fill-white" viewBox="0 0 20 21"><path d="M2.223 2.883c0-.487.395-.883.882-.883H17.34c.487 0 .883.396.883.883v14.234a.884.884 0 0 1-.883.883H3.105a.883.883 0 0 1-.882-.883V2.883Zm6.775 3.93a.356.356 0 0 0-.553.295v5.784a.356.356 0 0 0 .553.295l4.337-2.891a.356.356 0 0 0 0-.592l-4.337-2.89Z" /></svg>
                  {course.totalClasses} Clases
                </span>
                <span className="flex items-center gap-2 rounded-full bg-gray-800 px-3 py-2 text-xs font-medium text-white">
                  <svg className="h-4 w-4 fill-white" viewBox="0 0 21 20"><path d="M16.668 3.667h-3.716C12.579 2.7 11.601 2 10.445 2 9.29 2 8.312 2.7 7.94 3.667H4.223c-.978 0-1.778.75-1.778 1.666V17c0 .917.8 1.667 1.778 1.667h12.445c.977 0 1.777-.75 1.777-1.667V5.333c0-.916-.8-1.666-1.777-1.666Z" /></svg>
                  {course.totalModules} Módulos
                </span>
                <span className="flex items-center gap-2 rounded-full bg-gray-800 px-3 py-2 text-xs font-medium text-yellow-400">
                  <svg className="w-4 h-4" viewBox="0 0 14 18" fill="#facc15"><path d="M12.672 8.2c-.23-.3-.51-.56-.77-.82-.67-.6-1.43-1.03-2.07-1.66C8.342 4.26 8.012 1.85 8.962 0c-.95.23-1.78.75-2.49 1.32-2.59 2.08-3.61 5.75-2.39 8.9.04.1.08.2.08.33 0 .22-.15.42-.35.5-.23.1-.47.04-.66-.12a.58.58 0 0 1-.14-.17c-1.13-1.43-1.31-3.48-.55-5.12C.792 7-.118 9.3.012 11.47c.06.5.12 1 .29 1.5.14.6.41 1.2.71 1.73 1.08 1.73 2.95 2.97 4.96 3.22 2.14.27 4.43-.12 6.07-1.6 1.83-1.66 2.47-4.32 1.53-6.6l-.13-.26c-.21-.46-.77-1.26-.77-1.26Z" /></svg>
                  {course.totalClasses * 25}
                </span>
              </div>

              {/* Description */}
              <p className="text-base font-light text-gray-400 leading-relaxed">{course.description}</p>

              {/* Start button */}
              <Link
                to={startLink}
                className="inline-flex items-center gap-2 rounded-md bg-green-500 bg-opacity-0 ring-2 ring-green-500 px-4 py-2.5 text-lg font-semibold text-green-400 transition-all hover:bg-opacity-100 hover:text-black"
              >
                Empezar curso
                <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3" /></svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== Course Content + Sidebar (AB layout) ===== */}
      <section className="relative pb-24 px-6">
        <MatrixBackground color="#00ff41" />
        <div className="absolute inset-0 bg-black/70 z-[1]" />
        <div className="relative z-10 max-w-7xl mx-auto flex flex-col justify-between gap-10 lg:flex-row lg:gap-12">

          {/* Left: Accordion */}
          <div className="w-full max-w-full rounded-xl bg-gray-900/70 border border-green-500/10 px-3 py-6 md:px-8 lg:flex-1">
            <h3 className="pb-5 text-2xl font-bold text-center text-white">Contenido del curso</h3>

            <div className="divide-y divide-green-500/10">
              {course.modules.map((mod, idx) => {
                const isOpen = openModules.has(idx);
                return (
                  <div key={idx} className="overflow-hidden">
                    <button
                      onClick={() => toggleModule(idx)}
                      className="group flex w-full items-start gap-3 px-4 py-3 text-left hover:text-green-400 transition-all"
                    >
                      {/* Flag number (AB style) */}
                      <div className="relative mt-0.5 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="32" viewBox="0 0 28 32" className={`h-7 transition-colors ${isOpen ? 'fill-green-400' : 'fill-white group-hover:fill-green-400'}`}>
                          <path d="M2.333 32.667H0V0h28l-6.767 10.5L28 21H2.333v11.667Z" />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center -translate-x-0.5 -translate-y-1 text-xs font-bold text-black">
                          {idx + 1}
                        </span>
                      </div>
                      <span className={`flex-1 text-base md:text-lg font-medium transition-colors ${isOpen ? 'text-green-400' : 'text-white group-hover:text-green-400'}`}>
                        {mod.title}
                      </span>
                      <svg className={`mt-1 h-5 w-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 fill-green-400' : 'fill-gray-500 group-hover:fill-green-400'}`} viewBox="0 0 22 12"><path d="M11 12 .608 0h20.784L11 12Z" /></svg>
                    </button>

                    {/* Expand/collapse content */}
                    <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
                      <ul className="space-y-1 px-5 pb-4 pl-14 text-sm md:text-base">
                        {mod.topics.map((topic, ti) => (
                          <li key={ti} className="flex items-center gap-3 py-1 text-gray-400">
                            <span className="h-1.5 w-1.5 rounded-full bg-green-500/50 flex-shrink-0" />
                            {topic}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Sidebar cards */}
          <div className="flex w-full flex-col gap-8 lg:max-w-sm lg:flex-col">
            {/* Progress / CTA card */}
            <div className="flex w-full flex-col gap-4 rounded-lg bg-gray-900/70 border border-green-500/10 p-5">
              <style>{`
                .progress-bar-striped {
                  background: repeating-linear-gradient(
                    -55deg,
                    #22c55e,
                    #22c55e 10px,
                    #16a34a 10px,
                    #16a34a 20px
                  );
                  background-size: 200% 100%;
                  animation: progress-shift 1.5s linear infinite;
                }
                @keyframes progress-shift {
                  0% { background-position: 0 0; }
                  100% { background-position: 40px 0; }
                }
              `}</style>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-400">
                    {hasStarted ? `${pct}% Completado` : 'Sin empezar'}
                  </span>
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    {hasStarted && totalLessons > 0 && (
                      <span className="text-green-400 font-semibold">{completedCount}/{totalLessons}</span>
                    )}
                    <svg className="h-3.5 w-3.5 fill-gray-500" viewBox="0 0 20 21"><path d="M2.223 2.883c0-.487.395-.883.882-.883H17.34c.487 0 .883.396.883.883v14.234a.884.884 0 0 1-.883.883H3.105a.883.883 0 0 1-.882-.883V2.883Zm6.775 3.93a.356.356 0 0 0-.553.295v5.784a.356.356 0 0 0 .553.295l4.337-2.891a.356.356 0 0 0 0-.592l-4.337-2.89Z" /></svg>
                    {totalTopics} temas
                  </div>
                </div>
                <div className="h-3 w-full rounded-full bg-gray-800 overflow-hidden border border-gray-700/50">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ease-out ${pct > 0 ? 'progress-bar-striped' : ''}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
              <Link to={startLink} className="flex items-center justify-center gap-2 rounded-lg bg-green-500 py-2.5 text-base font-semibold text-black transition-colors hover:bg-green-400">
                {hasStarted ? (
                  pct >= 100 ? (
                    <><svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>Repasar curso</>
                  ) : (
                    <><svg className="h-4 w-4 fill-current" viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3" /></svg>Continuar donde dejaste</>
                  )
                ) : (
                  <><svg className="h-4 w-4 fill-current" viewBox="0 0 24 24"><polygon points="6 3 20 12 6 21 6 3" /></svg>Empezar curso</>
                )}
              </Link>
              {hasStarted && pct < 100 && totalLessons > 0 && (
                <p className="text-xs text-center text-gray-500">
                  Ejercicio {Math.min(resumeIdx + 1, totalLessons)} de {totalLessons}
                </p>
              )}
            </div>

            {/* Skills card */}
            <div className="flex w-full flex-col rounded-lg bg-gray-900/70 border border-green-500/10 overflow-hidden">
              <div className="flex w-full gap-3 rounded-t-lg bg-gray-900 px-6 py-4 text-lg font-semibold text-white">
                <div className="h-min rounded-sm bg-white/10 p-1">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none"><path fill="#22c55e" d="M21 20.714h-5.143V4H21v16.714Zm-3.857-1.285h2.571V5.286h-2.571v14.143Zm-2.572 1.285H9.43V9.143h5.142v11.571Zm-6.428 0H3V13h5.143v7.714Z" /></svg>
                </div>
                Lo que vas a aprender
              </div>
              <div className="px-6 py-5 space-y-2.5">
                {course.skills.map((skill, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-gray-300 leading-relaxed">
                    <svg className="h-4 w-4 flex-shrink-0 mt-0.5 fill-green-500" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" /></svg>
                    {skill}
                  </div>
                ))}
              </div>
            </div>

            {/* Tools card */}
            <div className="flex w-full flex-col rounded-lg bg-gray-900/70 border border-green-500/10 overflow-hidden">
              <div className="flex w-full gap-3 rounded-t-lg bg-gray-900 px-6 py-4 text-lg font-semibold text-white">
                <div className="h-min rounded-sm bg-white/10 p-1">
                  <svg className="w-5 h-5 stroke-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2}><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
                </div>
                Herramientas
              </div>
              <div className="px-6 py-5 space-y-2.5">
                {course.tools.length > 0 ? (
                  course.tools.map((tool, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm text-gray-300 leading-relaxed">
                      <svg className="h-4 w-4 flex-shrink-0 mt-0.5 fill-green-500" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" /></svg>
                      {tool}
                    </div>
                  ))
                ) : (
                  <div className="flex items-start gap-2.5 text-sm text-gray-400 leading-relaxed">
                    <svg className="h-4 w-4 flex-shrink-0 mt-0.5 fill-green-500" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" className="stroke-green-500" /></svg>
                    Todo incluido en la plataforma. No necesitás instalar nada.
                  </div>
                )}
              </div>
            </div>

            {/* Prerequisites */}
            <div className="flex w-full flex-col rounded-lg bg-gray-900/70 border border-green-500/10 overflow-hidden">
              <div className="flex w-full gap-3 rounded-t-lg bg-gray-900 px-6 py-4 text-lg font-semibold text-white">
                <div className="h-min rounded-sm bg-white/10 p-1">
                  <svg className="w-5 h-5 stroke-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                </div>
                Requisitos
              </div>
              <div className="px-6 py-5 space-y-2.5">
                {course.prerequisites.map((req, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-gray-300 leading-relaxed">
                    <svg className="h-4 w-4 flex-shrink-0 mt-0.5 fill-green-500" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" /></svg>
                    {req}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
